import classNames from "classnames";
import type { ReactNode } from "react";
import { Link, useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import { buttonVariants } from "~/components/ui/button";
import { formatPrice } from "~/utils/formatPrice";

export function PublicProductCard({
  name,
  description,
  specimenType,
  sellingPrice,
  to,
  extraContent,
}: {
  name: string;
  description?: string;
  specimenType?: "BLOOD" | "SALIVA" | "URINE" | "STOOL" | string;
  sellingPrice?: number;
  to?: string;
  extraContent?: ReactNode;
}) {
  const { t } = useTranslation();
  const location = useLocation();
  const specimenTypeLabelMap: Record<string, string> = {
    BLOOD: t("Kapillarblutentnahme"),
    SALIVA: t("Speichelprobe"),
    URINE: t("Urinprobe"),
    STOOL: t("Stuhlprobe"),
  };

  const content = (
    <article className="flex h-full flex-col overflow-hidden rounded-3xl border bg-white shadow-xl transition hover:-translate-y-1 hover:shadow-2xl">
      <div
        className={classNames("px-8 py-3 font-semibold text-white", {
          "bg-dynamic-primary": specimenType === "BLOOD" || !specimenType,
          "bg-green-500": specimenType === "URINE",
          "bg-yellow-500": specimenType === "SALIVA",
          "bg-amber-800": specimenType === "STOOL",
        })}
      >
        {specimenTypeLabelMap[specimenType ?? "BLOOD"] ?? specimenTypeLabelMap.BLOOD}
      </div>
      <div className="flex flex-1 flex-col px-6 py-8 pb-4">
        <h2 className="mb-2 text-xl font-bold">{name}</h2>
        {description ? <p className="mb-4 line-clamp-3 text-gray-600">{description}</p> : null}
        {sellingPrice != null ? <p className="text-2xl font-bold">{formatPrice(sellingPrice)} €</p> : null}
        {extraContent ? <div className="flex-1 pt-4">{extraContent}</div> : <div className="flex-1" />}
      </div>
      {to ? (
        <div className="w-full p-4 pt-0">
          <span className={buttonVariants({ variant: "outline", className: "w-full font-bold" })}>
            {t("Mehr Infos")}
          </span>
        </div>
      ) : null}
    </article>
  );

  if (!to) return content;

  return (
    <Link
      to={to}
      state={{ from: location.pathname + location.search }}
      className="block h-full focus:outline-none focus-visible:ring-2 focus-visible:ring-dynamic-primary"
    >
      {content}
    </Link>
  );
}
